import { useState } from "react";
import WarningIcon from "@mui/icons-material/Warning";

// COMPONENTS
import KOGCollapse from "@/main/components/shared/kogCollapse/kogCollapse";
import KOGImage from "@/main/components/shared/kogImage/kogImage";
import { SpinnerLoading } from "@/main/components/shared/styles";

// SERVICES
import {
	QuoteNoSupplierAssignedItemResponse,
	QuoteNoSupplierAssignedResponse,
	QuoteSelectSuppliersDataResponseItems,
} from "@/main/models/response";
import { IMAGE_DEFAULT } from "@/main/services/constants";
import { quoteApi } from "@/main/api";
import { getTranslateByKey, isNullUndefined, isShowControlByElementId } from "@/main/services/utils";
import { formatCurrency } from "@/main/services/utilsNumber";
import { Controls } from "@/main/services/constants/constantsControl";
import QuoteProcurementPartInfo, {
	getTooltipFormatted,
} from "@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfo";

// STYLES & LOCAL COMPONENTS
import RowSupplierAssigned from "./components/rowSupplierAssigned/rowSupplierAssigned";
import RemainingTimeShippingTable from "./components/remainingTimeShippingTable/remainingTimeShippingTable";
import * as S from "./styles";
import ButtonDeletePart from "./components/buttonDeletePart";
import ButtonRequotePart from "./components/buttonRequotePart";
import { AddCartType, RefreshQuoteByGetItemsType } from "./components/types";
import { ProcurementStatusEnum } from "@/main/services/enum/StatusProcurement";
import { CartState } from "@/main/store/cart";

interface DetailsCartSupplierAssignedProps {
	cart: QuoteNoSupplierAssignedResponse | null;
	part: QuoteNoSupplierAssignedItemResponse;
	idProcurementStatus: number;
	guidQuote: string;
	integration: boolean;
	isQuoteBlocked: boolean;
	isPaymentPending: boolean;
	dataCart: CartState;
	canShowButtonRemovePart: boolean;
	canShowButtonRequotePart: boolean;
	loadCart: (guidQuote: string) => void;
	addCart: AddCartType;
	refreshQuoteByGetItems: RefreshQuoteByGetItemsType;
}
function DetailsCartSupplierAssigned({
	part,
	addCart,
	dataCart,
	canShowButtonRemovePart,
	canShowButtonRequotePart,
	guidQuote,
	integration,
	isPaymentPending,
	isQuoteBlocked,
	idProcurementStatus,
	refreshQuoteByGetItems,
}: DetailsCartSupplierAssignedProps) {
	const showPrices = isShowControlByElementId(Controls.IdShowPrices);
	const [isLoadingParts, setisLoadingParts] = useState(false);
	const [dataParts, setDataParts] = useState<QuoteSelectSuppliersDataResponseItems | null>(null);

	function fetchPartsAssigned(isCollapsed: boolean, partAssigned: QuoteNoSupplierAssignedItemResponse) {
		if (isLoadingParts || !isCollapsed) return;
		if (!isNullUndefined(dataParts)) return;
		setisLoadingParts(true);

		quoteApi
			.getSupplierItems(partAssigned.guidQuoteItem)
			.then((response) => {
				const {
					data: { result },
				} = response;
				setDataParts(result);
			})
			.catch(() => {})
			.finally(() => {
				setisLoadingParts(false);
			});
	}
	const showButtonRemovePart = () => canShowButtonRemovePart && !isPaymentPending;
	const showButtonRequotePart = () => canShowButtonRequotePart && !isQuoteOpen;
	const isQuoteOpen = idProcurementStatus === ProcurementStatusEnum.Open;
	const isQuoteCanceled = idProcurementStatus === ProcurementStatusEnum.Cancelled;
	const isQuoteManual =
		idProcurementStatus === ProcurementStatusEnum.Manual ||
		idProcurementStatus === ProcurementStatusEnum.WaitingRelease;

	return (
		<KOGCollapse
			key={part.guidQuoteItem}
			customIcon={isLoadingParts ? <SpinnerLoading noMargin size="xsmall" /> : undefined}
			onCustomCollapse={(isCollapsed) => {
				fetchPartsAssigned(isCollapsed, part);
			}}
			titleTooltipIcon={{
				showLess: getTranslateByKey("information.showLess"),
				showMore: getTranslateByKey("information.clicktoseetheofers"),
			}}
			header={
				<S.BodyBox key={part.guidQuoteItem} style={{ position: "initial" }}>
					<S.ContainerPart>
						<KOGImage width={80} height={80} src={IMAGE_DEFAULT} />
						<S.PartInfo title={getTooltipFormatted(part.quoteProcurementPartInfo)}>
							<QuoteProcurementPartInfo
								isTooltip
								data={part.quoteProcurementPartInfo}
								isEllipsisText={false}
								renderType="column"
								showLineSupplierInfo={{ info: "2lines" }}
								showIconNotes
							/>
						</S.PartInfo>
						{showPrices && !isNullUndefined(part.priceCap) && (
							<S.TextPriceCap>
								{`${getTranslateByKey("information.priceCap")}: ${formatCurrency(part.priceCap)}`}
							</S.TextPriceCap>
						)}
					</S.ContainerPart>
					<S.AreaButtonAbsolute>
						{showButtonRequotePart() && (
							<ButtonRequotePart
								type="unaswered"
								dataCart={dataCart}
								refreshQuoteByGetItems={refreshQuoteByGetItems}
								isQuoteBlocked={isQuoteBlocked}
								isQuoteCanceled={isQuoteCanceled}
								part={{
									guidQuoteItem: part.guidQuoteItem,
									idPartType: part.idPartType,
									replacePartType: false,
								}}
								idButtonRequotePart={`id-createOrder-button-requoteProduct-noSupplierAssigned-${part.guidQuoteItem}`}
								isRequotePartCanceled={false}
							/>
						)}
						{showButtonRemovePart() && (
							<ButtonDeletePart
								type="noSupplierAssigned"
								dataCart={dataCart}
								refreshQuoteByGetItems={refreshQuoteByGetItems}
								guidQuote={guidQuote}
								isQuoteBlocked={isQuoteBlocked}
								isQuoteCanceled={isQuoteCanceled}
								isPaymentPending={isPaymentPending}
								guidQuoteItem={part.guidQuoteItem}
								idButtonDeletePart={`id-createOrder-button-deleteProduct-noSupplierAssigned-${part.guidQuoteItem}`}
							/>
						)}
					</S.AreaButtonAbsolute>
				</S.BodyBox>
			}
			content={
				<RowSupplierAssigned
					addCart={addCart}
					dataCart={dataCart}
					integration={integration}
					isQuoteManual={isQuoteManual}
					isQuoteOpen={isQuoteOpen}
					isWaitingRelease={idProcurementStatus === ProcurementStatusEnum.WaitingRelease}
					dataParts={dataParts}
					isLoading={isLoadingParts}
					externalPartId={part.externalPartId}
					guidPart={part.guidPart}
					guidQuote={String(guidQuote)}
				/>
			}
		/>
	);
}
interface IBoxCartNoSupplierAssignedProps extends Omit<DetailsCartSupplierAssignedProps, "part"> {
	allowCloseOfferRequest?: boolean;
}
function BoxCartNoSupplierAssigned(props: IBoxCartNoSupplierAssignedProps) {
	if (isNullUndefined(props.cart) || props.cart.parts.length === 0) return null;
	const { parts } = props.cart;

	return (
		<S.ContainerBox>
			<S.HeaderBox>
				<S.ContainerTitleBox>
					<S.TitleBox>{getTranslateByKey("information.shoppingTable")} -</S.TitleBox>
					<S.TitleBoxWarning>
						{props.cart.label}
						<WarningIcon fontSize="small" color="error" />
					</S.TitleBoxWarning>

					<RemainingTimeShippingTable
						dataCart={props.dataCart}
						cart={props.cart}
						loadCart={props.loadCart}
						guidQuote={props.guidQuote}
						allowCloseOfferRequest={props.allowCloseOfferRequest}
						type="noSupplierAssigned"
					/>
				</S.ContainerTitleBox>
			</S.HeaderBox>
			<S.ContentBox>
				{parts.map((part) => (
					<DetailsCartSupplierAssigned key={part.guidQuoteItem} {...props} part={part} />
				))}
			</S.ContentBox>
		</S.ContainerBox>
	);
}

export default BoxCartNoSupplierAssigned;
